/* eslint-disable max-classes-per-file */

/**
 * @file 资产的 WS 跟 现货使用的 WS 订阅机制不一样，无法使用现货现有的 WS 包，所以临时写一个。后期会修改 bone-websocket 让其支持自定义交互行为
 */
import BackOff from 'backo2';

/**
 * 钩子类
 */
class Hook {
    constructor() {
        // 事件池
        this.eventPool = new Map();
    }

    on(eventName, handler) {
        if (typeof handler !== 'function') {
            return () => { };
        }

        if (!this.eventPool.has(eventName)) {
            this.eventPool.set(eventName, new Set());
        }

        const handlerList = this.eventPool.get(eventName);
        handlerList.add(handler);

        const unHook = () => this.off(eventName, handler);
        return unHook;
    }

    off(eventName, handler) {
        const handlerList = this.eventPool.get(eventName);

        if (typeof handlerList === 'undefined') {
            return;
        }

        if (typeof handler === 'undefined') {
            this.eventPool.delete(eventName);
            return;
        }

        if (handlerList.has(handler)) {
            handlerList.delete(handler);
            // 没有事件了，移除 handlerList
            if (handlerList.size === 0) {
                this.eventPool.delete(eventName);
            }
        }
    }

    has(eventName) {
        return this.eventPool.has(eventName);
    }
}

/**
 * 同步并行钩子
 */
class SyncHook extends Hook {
    once(eventName, handler) {
        const unHook = this.on(eventName, (data) => {
            handler(data);
            unHook();
        });
        return unHook;
    }

    emit(eventName, data) {
        const handlerList = this.eventPool.get(eventName);

        if (typeof handlerList !== 'undefined') {
            handlerList.forEach((handler) => handler(data));
        }
    }
}

// Socket 状态枚举
const socketStateEnum = {
    INIT: -1,
    CONNECTING: 0,
    OPEN: 1,
    ERROR: 2,
    CLOSED: 3,
};

// hooks 枚举
const hooksEnum = {
    // 用户主动关闭 ws
    USER_CLOSE: 'userClose',
    // 用户鉴权
    USER_LOGIN: 'login',
    // ws 已经准备好了
    WS_READY: 'ready',
    // ws 已经 open
    WS_OPEN: 'open',
    // ws 关闭了
    WS_CLOSE: 'close',
    // ws 报错了
    WS_ERROR: 'error',
};

export default class OptionWS extends SyncHook {
    constructor(options = {}) {
        super();
        this.options = {
            pingInterval: 10 * 1000, // ping 间隔
            reconnectMaxCount: 5, // 最大重试5次
            getToken: null, // 获取token的方法
            ...options,
        };
        this.state = socketStateEnum.INIT;
        this.isLogin = false;
        this.token = '';
        this.subId = 0;
        this.subIdCache = new Map();
        this.socket = null;
        this.openTime = 0;
        this.reconnectTimer = null;
        this.pingTimer = null;
        this.backOff = new BackOff({
            min: 1000,
            max: 6000,
        });

        if (this.options.autoConnect) {
            this.connect();
        }
        this.bindEvents();
    }

    connect({ reset = false } = {}) {
        // 如果要求重置，则重新设置下重试的计数
        if (reset) this.backOff.reset();

        if ([socketStateEnum.CONNECTING, socketStateEnum.OPEN].includes(this.state)) {
            return;
        }

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
        }

        this.state = socketStateEnum.CONNECTING;
        this.openTime = Date.now();

        const { url } = this.options;
        const publicData = { url };
        const ws = new WebSocket(url);

        ws.onopen = async () => {
            const data = {
                duration: Date.now() - this.openTime,
                ...publicData,
            };
            this.state = socketStateEnum.OPEN;
            this.emit(hooksEnum.WS_READY);
            this.emit(hooksEnum.WS_OPEN, data);
            this.ping();
            this.backOff.reset();

            // 如果是断连后重新连接，就重新订阅一遍之前订阅的 topic
            // 私有推送时，需重新获取token信息
            if (this.subIdCache.size) {
                if (typeof this.options.getToken === 'function') {
                    this.token = await this.options.getToken();
                    if (this.token) {
                        this.send({ op: 'login', args: [this.token] });
                        this.subIdCache.forEach((subId, topic) => {
                            this.send({ op: 'subscribe', args: [topic], id: `${subId}` });
                        });
                    }
                    return;
                }
                this.subIdCache.forEach((subId, topic) => {
                    this.send({ op: 'subscribe', args: [topic], id: `${subId}` });
                });
            }
        };

        ws.onerror = () => {
            this.state = socketStateEnum.ERROR;
            this.emit(hooksEnum.WS_ERROR, publicData);
        };

        ws.onclose = () => {
            if (this.state === socketStateEnum.CLOSED) {
                return;
            }

            // 不是用户主动关闭的，主动触发重连机制
            this.state = socketStateEnum.ERROR;
            this.emit(hooksEnum.WS_CLOSE, publicData);
            this.reconnect();
        };

        ws.onmessage = (event) => {
            const {
                op, topic, data, args, type, success,
            } = JSON.parse(event.data);

            if (op === 'ping') {
                this.send({ op: 'pong', args });
                return;
            }

            if (type === 'AUTH_RESP' && success === true) {
                // 登录鉴权成功
                this.emit(hooksEnum.USER_LOGIN);
                this.isLogin = true;
                return;
            }

            if (typeof topic === 'string' && topic !== '') {
                const res = {
                    code: 200,
                    data: data.result ?? data,
                    orgData: data,
                };
                this.emit(topic, res);
            }
        };

        this.socket = ws;
    }

    bindEvents() {
        const reconnectHandle = () => {
            // 1. 页面不可见时不重连
            // 2. 断网情况不重连，`onLine` 可能不支持，所以使用 `=== false` 判断
            if (document.hidden || navigator.onLine === false) return;
            this.connect({ reset: true });
        };
        document.addEventListener('visibilitychange', reconnectHandle);
        window.addEventListener('online', reconnectHandle);

        this.on(hooksEnum.USER_CLOSE, () => {
            document.removeEventListener('visibilitychange', reconnectHandle);
            window.removeEventListener('online', reconnectHandle);
        });
    }

    ping() {
        this.pingTimer = setTimeout(() => {
            this.send({ op: 'ping', args: [Date.now()] });
            // eslint-disable-next-line no-unused-vars
            this.ping();
        }, this.options.pingInterval);
    }

    ready() {
        return new Promise((done) => {
            if (this.state === socketStateEnum.OPEN) {
                done();
            } else {
                this.on(hooksEnum.WS_READY, () => done());
            }
        });
    }

    getSubId(topic) {
        if (this.subIdCache.has(topic)) {
            return this.subIdCache.get(topic);
        }

        this.subId += 1;
        this.subIdCache.set(topic, this.subId);
        return this.subId;
    }

    sub({ topic }, handler) {
        if (!this.has(topic)) {
            this.ready().then(() => {
                const subId = this.getSubId(topic);
                this.send({ op: 'subscribe', args: [topic], id: `${subId}` });
            });
        }
        this.on(topic, handler);

        const unsub = () => this.unsub({ topic }, handler);
        return unsub;
    }

    unsub({ topic }, handler) {
        this.ready().then(() => {
            this.off(topic, handler);

            if (!this.has(topic)) {
                const subId = this.getSubId(topic);
                this.send({ op: 'unsubscribe', args: [topic], id: `${subId}` });
                this.subIdCache.delete(topic);
            }
        });
    }

    login(token, callback) {
        this.ready().then(() => {
            let cb = () => { };
            if (typeof token === 'function') {
                cb = token;
            }
            cb = typeof callback === 'function' ? callback : cb;

            if (this.isLogin) {
                cb();
                return;
            }

            if (!this.has(hooksEnum.USER_LOGIN) && typeof token === 'string') {
                this.send({ op: 'login', args: [token] });
                this.token = token;
            }

            this.once(hooksEnum.USER_LOGIN, cb);
        });
        return this;
    }

    send(data) {
        this.ready().then(() => {
            this.socket.send(JSON.stringify(data));
        });
    }

    close() {
        if (this.state === socketStateEnum.CLOSED) {
            return;
        }
        if (this.pingTimer) {
            clearTimeout(this.pingTimer);
        }
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
        }
        this.state = socketStateEnum.CLOSED;
        this.emit(hooksEnum.USER_CLOSE);
        this.subIdCache.clear();
        this.off();
        this.socket.close();
    }

    reconnect() {
        if ([socketStateEnum.OPEN, socketStateEnum.CONNECTING].includes(this.state) || this.backOff.attempts >= this.options.reconnectMaxCount) {
            return;
        }

        this.reconnectTimer = setTimeout(() => this.connect(), this.backOff.duration());
    }

    /**
     * 切换线路连接
     *
     * @param {Object} options 配置对象
     */
    changeLine(options) {
        this.options = {
            ...this.options,
            ...options,
        };
        this.connect({ reset: true });
    }
}
